import { useMemo } from "react";

import { useGetQueryData } from "@/utils/react-query-hooks/general";
import * as TempWidgets from "./tempWidgets/_EXPORTS";
import { TEMP_WIDGET_PROPS_KEY } from "./DATA_KEYS";

/**
 * Adapter over the temp widget registry — the same job ReportLanding/CONFIG.js
 * does for top-level widgets, scoped to this container.
 */

/** Options for the "Temp Widget" picker. `value` IS the export name. */
export const TEMP_WIDGET_OPTIONS = Object.entries(TempWidgets).map(([key, mod]) => ({
  ...(mod.optionDef || {}),
  value: key,
}));

/**
 * The sub-widgets THIS tenant may pick, as opposed to every one that exists.
 *
 * Same split — and same reason — as allWidgetsBaseConfs vs
 * useAddableWidgetsBaseConfs one level up: TEMP_WIDGET_OPTIONS must stay
 * UNFILTERED because getTempWidgetOption resolves a SAVED widget's title out of
 * it (useWidgetTitle, processRawConfigFn). Filter the picker, never the
 * registry, or a Priorities widget already on a report goes untitled the moment
 * its tenant stops qualifying.
 *
 * A sub-widget opts in with `isAvailableToAdd({ platformConfigs })` on its
 * optionDef (see tempWidgets/TempPriorities). No predicate = available to every
 * tenant that can see the container at all.
 */
export const useAddableTempWidgetOptions = () => {
  const platformConfigs = useGetQueryData("platformConfigs");

  return useMemo(
    () =>
      TEMP_WIDGET_OPTIONS.filter(
        ({ isAvailableToAdd }) =>
          !isAvailableToAdd || isAvailableToAdd({ platformConfigs })
      ),
    [platformConfigs]
  );
};

/** The registered module, or null for "nothing picked" / a removed widget. */
export const getTempWidget = (tempWidget) =>
  (tempWidget && TempWidgets[tempWidget]) || null;

export const getTempWidgetOption = (tempWidget) =>
  TEMP_WIDGET_OPTIONS.find((o) => o.value === tempWidget) || null;

/**
 * The container's widget config, re-shaped so the TEMP WIDGET sees its own
 * props slice as `props`. That is what lets a temp widget's processRawConfigFn
 * / someReqPropsUnset / ConfigForm be written exactly like a top-level
 * widget's — they never learn they are nested.
 */
export const asTempWidgetConfig = (widgetConfig) => ({
  ...widgetConfig,
  props: widgetConfig?.props?.[TEMP_WIDGET_PROPS_KEY] || {},
});
